"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedArticle() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="mb-24 w-full">
        <div className="aspect-[21/9] w-full animate-pulse bg-muted/40" />
      </div>;
  }
  if (!posts || posts.length === 0) return null;
  const featured = posts[0];
  const category = featured.categories && featured.categories.length > 0 ? featured.categories[0] : null;
  const dateStr = featured.date ? new Date(featured.date).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric"
  }) : "";
  const authorName = featured.author?.name || "Forever Fresh Team";
  return <Link to={featured.link || "#"} className="group relative mb-24 block w-full overflow-hidden rounded-lg shadow-lg">
      <div className="relative aspect-[21/9] w-full overflow-hidden">
        {featured.featuredImage ? <img src={featured.featuredImage} alt={featured.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /> : <div className="h-full w-full bg-muted" />}
        <div className="absolute inset-x-0 bottom-0 h-2/3" style={{
        background: "linear-gradient(to top, hsl(120 38% 14%) 0%, hsl(120 38% 14% / 0.6) 50%, transparent 100%)"
      }} />
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
          {category && <span className="mb-4 inline-block font-default text-xs uppercase tracking-widest text-green-400">
              {category.name}
            </span>}
          <h3 className="font-default italic font-light leading-[1.1] text-2xl md:text-4xl lg:text-[2.5rem] max-w-4xl text-white">
            {featured.title}
          </h3>
        </div>
      </div>
      <p className="mt-6 font-default text-xs uppercase tracking-widest text-muted-foreground px-8 pb-8">
        {authorName} · 12 min read · {dateStr}
      </p>
    </Link>;
}
function MasonryGrid() {
  const {
    posts,
    loading,
    hasNext,
    loadMore,
    isRefetching,
    total
  } = usePosts();
  if (loading) {
    return <div className="columns-1 gap-x-8 md:columns-2 lg:columns-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="mb-12 break-inside-avoid">
            <div className="aspect-[4/5] w-full animate-pulse bg-muted/40 rounded-lg" />
            <div className="mt-6 h-4 w-1/3 animate-pulse bg-muted/40" />
            <div className="mt-4 h-6 w-full animate-pulse bg-muted/40" />
          </div>)}
      </div>;
  }
  const gridPosts = posts.slice(1);
  if (gridPosts.length === 0) {
    return <p className="text-center font-default text-sm uppercase tracking-widest text-muted-foreground">
        No additional awareness updates available.
      </p>;
  }
  const aspectClasses = ["aspect-[4/5]", "aspect-square", "aspect-[16/10]", "aspect-[4/5]", "aspect-[16/10]", "aspect-square"];
  const headlineSizes = ["text-2xl", "text-xl", "text-[1.375rem]", "text-2xl", "text-xl", "text-[1.375rem]"];
  return <>
      <div className="columns-1 gap-x-8 md:columns-2 lg:columns-3">
        {gridPosts.map((post, i) => {
        const category = post.categories && post.categories.length > 0 ? post.categories[0] : null;
        const dateStr = post.date ? new Date(post.date).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric"
        }) : "";
        const authorName = post.author?.name || "Forever Fresh Team";
        const aspectClass = aspectClasses[i % aspectClasses.length];
        const headlineSize = headlineSizes[i % headlineSizes.length];
        const cleanExcerpt = post.excerpt ? post.excerpt.replace(/<[^>]*>/g, "").trim() : "";
        return <Link key={post.id} to={post.link || "#"} data-index={i} className="group mb-12 block break-inside-avoid">
              <div className={`relative w-full overflow-hidden rounded-lg shadow-md ${aspectClass}`}>
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /> : <div className="h-full w-full bg-muted" />}
              </div>
              <div className="mt-6">
                {category && <span className="mb-4 inline-block border-l-2 border-green-600 pl-3 font-default text-[0.6875rem] uppercase tracking-widest text-green-800">
                    {category.name}
                  </span>}
                <h3 className={`font-default font-normal leading-[1.2] text-foreground line-clamp-2 ${headlineSize}`}>
                  <span className="bg-[length:0%_1px] bg-left-bottom bg-no-repeat bg-gradient-to-r from-green-600 to-green-600 transition-[background-size] duration-500 group-hover:bg-[length:100%_1px]">
                    {post.title}
                  </span>
                </h3>
                {cleanExcerpt && <p className="mt-3 font-default text-sm text-muted-foreground line-clamp-1">
                    {cleanExcerpt}
                  </p>}
                <p className="mt-4 font-default text-[0.6875rem] uppercase tracking-widest text-muted-foreground">
                  {authorName} · 8 min read · {dateStr}
                </p>
              </div>
            </Link>;
      })}
      </div>

      <div className="mt-20 flex flex-col items-center justify-center gap-4">
        <p className="font-default text-xs uppercase tracking-widest text-muted-foreground">
          Showing {posts.length} of {total}
        </p>
        {hasNext && <Button onClick={async () => await loadMore()} disabled={isRefetching} variant="outline" size="lg" className="group rounded-lg border-green-800 bg-transparent px-10 py-6 font-default text-xs uppercase tracking-widest text-green-800 hover:bg-green-800 hover:text-white">
            {isRefetching ? "Loading..." : "Load More Updates"}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>}
      </div>
    </>;
}
export default function AwarenessGrid() {
  const wp_query_params = {
    post_type: "post",
    per_page: 12,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="3355" id="environmentalawarenessgrid" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-20 flex flex-col gap-6 md:mb-24 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-6 font-default text-xs uppercase tracking-widest text-green-700">
              Forever Fresh — Awareness Hub
            </p>
            <h2 className="font-default text-4xl font-light leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-7xl">
              Keeping Issues <span className="italic text-green-700">Forever Fresh</span>
            </h2>
          </div>
          <p className="max-w-md font-default text-base leading-relaxed text-muted-foreground">
            We bridge the gap between urgent humanitarian causes and modern advocacy. Explore our latest insights, impact reports, and mission-driven stories.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 12,
        order: "desc",
        orderby: "date"
      }}>
          <FeaturedArticle />
          <MasonryGrid />
        </PostsProvider>
      </div>
    </section>;
}